import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Scale, Plus, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ComparePage = () => {
  const [searchParams] = useSearchParams();
  const [compareType, setCompareType] = useState(searchParams.get("type") || "character");
  const [allItems, setAllItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [universes, setUniverses] = useState([]);

  useEffect(() => {
    const fetchUniverses = async () => {
      try {
        const res = await axios.get(`${API}/universes`);
        setUniverses(res.data);
      } catch { /* silent */ }
    };
    fetchUniverses();
  }, []);

  useEffect(() => {
    fetchItems();
    setSelected([]);
  }, [compareType]);

  const fetchItems = async () => {
    try {
      const endpointMap = { character: "characters", universe: "universes", weapon: "weapons" };
      const res = await axios.get(`${API}/${endpointMap[compareType]}`);
      setAllItems(res.data);
    } catch {
      toast.error("Failed to load items");
    }
  };

  const addItem = (item) => {
    if (selected.length >= 4) { toast.error("Maximum 4 items"); return; }
    if (selected.find(s => s.id === item.id)) return;
    setSelected(prev => [...prev, item]);
  };

  const removeItem = (id) => {
    setSelected(prev => prev.filter(s => s.id !== id));
  };

  const getUniverseName = (uid) => universes.find(u => u.id === uid)?.name || "";

  const filtered = allItems.filter(item => {
    const name = item.name?.toLowerCase() || "";
    return name.includes(searchQuery.toLowerCase()) && !selected.find(s => s.id === item.id);
  });

  const fields = compareType === "character"
    ? [
        { key: "species", label: "Species" },
        { key: "affiliation", label: "Affiliation" },
        { key: "abilities", label: "Abilities", isArray: true },
        { key: "description", label: "Description", isLong: true },
      ]
    : compareType === "universe"
    ? [
        { key: "category", label: "Category" },
        { key: "formed_date", label: "Formed" },
        { key: "time_period", label: "Time Period" },
        { key: "description", label: "Description", isLong: true },
      ]
    : [
        { key: "type", label: "Type" },
        { key: "description", label: "Description", isLong: true },
      ];

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title="Compare" description="Compare characters, universes, and weapons side by side." />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-6xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/5 mb-3">
            <Scale className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-xs font-mono tracking-wider text-emerald-500 uppercase">Side by Side</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-2">Compare</h1>
          <p className="text-slate-400 text-sm">Put characters, universes, or weapons head to head</p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 justify-center mb-8">
          {["character", "universe", "weapon"].map(t => (
            <button
              key={t}
              onClick={() => setCompareType(t)}
              className={`px-4 py-2 text-sm rounded-lg transition-all capitalize ${
                compareType === t
                  ? "bg-emerald-500/20 text-emerald-500 border border-emerald-500/30"
                  : "bg-white/5 text-slate-400 border border-white/10 hover:text-white"
              }`}
              data-testid={`compare-type-${t}`}
            >
              {t === "character" ? "Characters" : t === "universe" ? "Universes" : "Weapons"}
            </button>
          ))}
        </div>

        {/* Search & Add */}
        <div className="card-surface p-4 mb-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${compareType}s to add...`}
                className="pl-9 input-dark"
                data-testid="compare-search"
              />
            </div>
          </div>
          {searchQuery && filtered.length > 0 && (
            <div className="mt-3 max-h-48 overflow-y-auto space-y-1" data-testid="compare-search-results">
              {filtered.slice(0, 10).map(item => (
                <button
                  key={item.id}
                  onClick={() => { addItem(item); setSearchQuery(""); }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 text-left transition-colors"
                  data-testid={`add-compare-${item.id}`}
                >
                  {item.image && <img src={item.image} alt="" className="w-8 h-8 rounded object-cover" />}
                  <div>
                    <p className="text-white text-sm">{item.name}</p>
                    {item.universe_id && <p className="text-slate-500 text-[10px]">{getUniverseName(item.universe_id)}</p>}
                  </div>
                  <Plus className="w-4 h-4 text-emerald-500 ml-auto" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Comparison Table */}
        {selected.length >= 2 ? (
          <div className="overflow-x-auto" data-testid="comparison-table">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left p-3 text-xs font-mono uppercase text-slate-500 w-32" />
                  {selected.map(item => (
                    <th key={item.id} className="p-3 min-w-[200px]">
                      <div className="card-surface p-3 text-center relative">
                        <button
                          onClick={() => removeItem(item.id)}
                          className="absolute top-2 right-2 w-5 h-5 rounded bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/40"
                          data-testid={`remove-compare-${item.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {item.image && (
                          <img src={item.image || item.cover_image} alt={item.name} className="w-16 h-16 rounded-full object-cover mx-auto mb-2 border-2 border-white/10" />
                        )}
                        <p className="text-white font-semibold font-['Outfit'] text-sm">{item.name}</p>
                        {item.universe_id && <p className="text-slate-500 text-[10px] mt-0.5">{getUniverseName(item.universe_id)}</p>}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fields.map(field => (
                  <tr key={field.key} className="border-t border-white/5">
                    <td className="p-3 text-xs font-mono uppercase text-amber-500 align-top">{field.label}</td>
                    {selected.map(item => {
                      const val = item[field.key];
                      return (
                        <td key={item.id} className="p-3 align-top" data-testid={`compare-cell-${item.id}-${field.key}`}>
                          <div className="card-surface p-3">
                            {field.isArray && Array.isArray(val) ? (
                              <div className="flex flex-wrap gap-1">
                                {val.map((v, i) => (
                                  <span key={i} className="text-[10px] bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded">{v}</span>
                                ))}
                                {val.length === 0 && <span className="text-slate-600 text-xs">—</span>}
                              </div>
                            ) : field.isLong ? (
                              <p className="text-slate-300 text-xs leading-relaxed line-clamp-4">{val || "—"}</p>
                            ) : (
                              <p className="text-slate-300 text-sm">{val || "—"}</p>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16 card-surface">
            <Scale className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">
              {selected.length === 0 ? `Search and add ${compareType}s above to compare` : `Add one more ${compareType} to start comparing`}
            </p>
            {selected.length === 1 && (
              <div className="mt-4 flex items-center justify-center gap-3">
                <div className="card-surface p-3 inline-flex items-center gap-2">
                  <p className="text-white text-sm">{selected[0].name}</p>
                  <span className="text-slate-500 text-sm">vs</span>
                  <span className="text-slate-600 text-sm">?</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Selected Tags */}
        {selected.length > 0 && selected.length < 2 && (
          <div className="flex gap-2 mt-4 justify-center">
            {selected.map(item => (
              <span key={item.id} className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs px-3 py-1.5 rounded-full">
                {item.name}
                <button onClick={() => removeItem(item.id)}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ComparePage;
